import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useTenant } from '../contexts/TenantContext';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: string;
  noindex?: boolean;
  schema?: Record<string, any> | Record<string, any>[];
}

export const SEO: React.FC<SEOProps> = ({
  title,
  description,
  keywords = "organization management, governance, community, nodes, collaboration",
  image = "/og-image.png",
  url,
  type = "website",
  noindex = false,
  schema
}) => {
  const { brand } = useTenant();
  const siteTitle = brand?.name || "App";
  const defaultDesc = `${siteTitle} is the definitive platform to manage communities, sports leagues, and non-profits. Centralize your comms, dues, and data securely.`;
  const finalDesc = description || defaultDesc;
  
  // Resolve base canonical URL: strip query parameters and hashes for cleanliness
  let finalUrl = url;
  if (!finalUrl && typeof window !== 'undefined') {
    const origin = window.location.origin;
    if (origin.includes('localhost') || origin.includes('127.0.0.1') || origin.includes('::1')) {
      finalUrl = 'https://orgsets.com' + window.location.pathname;
    } else {
      finalUrl = origin.replace(/^http:\/\//i, 'https://') + window.location.pathname;
    }
  }
  if (!finalUrl) {
    const domainPart = brand?.name.toLowerCase().replace(/\s+/g, '') || 'orgsets';
    finalUrl = `https://${domainPart}.com`;
  }
  
  // Always enforce HTTPS for canonical URLs to prevent http search indexing
  finalUrl = finalUrl.replace(/^http:\/\//i, 'https://');

  // Enforce canonical apex domain: replace www.orgsets.com with orgsets.com
  finalUrl = finalUrl.replace(/^https?:\/\/(?:www\.)?orgsets\.com/i, 'https://orgsets.com');
  
  // Normalize URL (strip trailing slash unless it's the root domain)
  if (finalUrl.endsWith('/') && finalUrl.length > 8) {
    finalUrl = finalUrl.slice(0, -1);
  }

  const fullTitleRaw = title ? (title.includes(siteTitle) ? title : `${title} | ${siteTitle}`) : siteTitle;
  
  // Truncate to optimal SEO lengths
  const fullTitle = fullTitleRaw.length > 60 ? `${fullTitleRaw.substring(0, 57)}...` : fullTitleRaw;
  const truncatedDesc = finalDesc.length > 160 ? `${finalDesc.substring(0, 157)}...` : finalDesc;

  return (
    <Helmet>
      {/* Standard Metadata */}
      <title>{fullTitle}</title>
      <meta name="description" content={truncatedDesc} />
      <meta name="keywords" content={keywords} />

      {/* Robots indexing instructions */}
      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow" />
      )}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={finalUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={truncatedDesc} />
      <meta property="og:image" content={image.startsWith('http') ? image : `https://orgsets.com${image}`} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={finalUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={truncatedDesc} />
      <meta name="twitter:image" content={image.startsWith('http') ? image : `https://orgsets.com${image}`} />

      {/* Canonical URL */}
      <link rel="canonical" href={finalUrl} />

      {/* Structured Data (JSON-LD) */}
      {schema && (
        <script type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      )}
    </Helmet>
  );
};
